
<script lang="ts" setup>import { computed as _computed } from 'vue';

import { Formula, Heading, Logo } from '@pohlcon/design-system';
import { useI18n } from 'vue-i18n';
import {
  ISOPROProductHeightOffsetVariantType,
  ISOPROProductSMVariantType
} from '~/models';
import type { Attribute, InstallationSetup } from '~/models';
import PrintAnnotation from './PrintAnnotation.vue';
import PrintAttribute from './PrintAttribute.vue';
import ProductDescription from './ProductDescription.vue';

const { t } = useI18n();

interface Props {
  selectedThickness: number | null;
  installationSetup: InstallationSetup;
  printOptions: Record<string, boolean>;
}

const props = defineProps<Props>();

const selectedWallStructure = _computed(() => props.installationSetup.wallStructure);
const selectedProduct = _computed(() => props.installationSetup.product);
const connectionTypeImg = _computed(() => {
  if (selectedProduct.value instanceof ISOPROProductHeightOffsetVariantType) {
    return `./assets/connectionType/M_${selectedProduct.value.variant}.png`;
  } else if (selectedProduct.value.type === 'SM' || selectedProduct.value.type === 'SQ') {
    if (selectedProduct.value instanceof ISOPROProductSMVariantType && selectedProduct.value.variant === 'WU') {
      return `./assets/connectionType/${selectedProduct.value.type}_${selectedProduct.value.variant}.png`;
    } else {
      return `./assets/connectionType/${selectedProduct.value.type}.png`;
    }
  } else {
    return './assets/connectionType/balconySlab.png';
  }
})

const attributes: Record<string, Attribute> = _computed(() => ({
  connectionType: {
    title: 'connectionType',
    value: t('printView.preview.attributes.connectionType.value'),
    img: connectionTypeImg.value ,
    toPrint: true
  },
  wallType: {
    title: 'wallType',
    value: t(selectedWallStructure.value.name),
    img: selectedWallStructure.value.imageURL,
    toPrint: true
  },
  wallThickness: {
    title: 'wallThickness',
    value: props.selectedThickness ? `${props.selectedThickness} ${t('units.distance')}` : 'None',
    toPrint: props.printOptions.wallThickness
  },
  category: {
    title: 'category',
    value: props.installationSetup.category || t('printView.preview.attributes.unknown'),
    className: 'category',
    toPrint: true
  },
  heatFlow: {
    title: 'heatFlow',
    value: selectedProduct.value.heatFlow?.toFixed(3) || 'Unknown',
    className: 'heatFlow',
    toPrint: !!selectedProduct.value?.heatFlow
  },
  lambda: {
    title: 'lambda',
    value: selectedProduct.value.lambdaValue?.toFixed(2) || t('printView.preview.attributes.unknown'),
    className: 'lambda',
    toPrint: props.printOptions.lambda
  }
}));
</script>

<template>
  <div class="preview-wrapper">
    <div class="page" id="print-section">
      <header>
        <Logo :brand="'PohlCon'" size="medium" />
        <Heading :level="2">PohlCon GmbH - Division H-Bau</Heading>
      </header>

      <div class="page__used">
        <div class="title-container container">
          <Heading :level="3">{{ t('printView.preview.titles.with_lambda1') }}</Heading>
          <Heading :level="4" v-html="t('printView.preview.titles.with_lambda2')" />
        </div>
        <div class="body-container container">
          <ProductDescription :product="selectedProduct" />
          <PrintAttribute :attribute="attributes.connectionType" />

          <PrintAttribute :attribute="attributes.wallType" />

          <PrintAttribute :attribute="attributes.wallThickness" />

          <PrintAttribute :attribute="attributes.heatFlow">
            <Formula v-if="selectedProduct.heatFlow" :latex="`${attributes.heatFlow.value} {W \\over K}`" size="small" />
          </PrintAttribute>

          <PrintAttribute :attribute="attributes.lambda">
            <Formula v-if="selectedProduct.lambdaValue" :latex="`${attributes.lambda.value} {W \\over m \\cdot K}`"
              size="small" />
          </PrintAttribute>

          <PrintAttribute
            :class="installationSetup.category || (!!selectedProduct.heatFlow && !installationSetup.category) ? '' : 'print__hide'"
            :attribute="attributes.category" v-slot="{ value: category }">
            <span :class="`${attributes.category.className}__none`"
              v-if="!!selectedProduct.heatFlow && !installationSetup.category">
              {{ t('printView.preview.attributes.category.noCat') }}
            </span>
            <span v-else :class="[attributes.category.className]">
              {{ category }}
              <sup v-if="installationSetup.isPrediction && installationSetup.category">{{ '*' }}</sup>
              <span v-if="installationSetup.category" class="appendix">
                {{ t('printView.preview.annotations.dUwb') }}
                <Formula
                  :latex="`\\Delta U_{WB} = ${attributes.category.value === 'B' ? 0.03 : 0.05} {W \\over m^2 \\cdot K}`"
                  size="small" />
              </span>
            </span>
          </PrintAttribute>
        </div>
        <PrintAnnotation :installationSetup="installationSetup" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '../../_variables' as *;

.preview-wrapper {
  width: 100%;
  position: relative;
  max-height: 72vh;

  @media screen and (max-width: $breakpoint-tablet) {
    max-height: 75vh;
  }
}

.page {
  font-size: 14px;
  padding: var(--gutter-xl);

  &__used {
    display: grid;
    grid-template-rows: auto auto 1fr;
    height: 100%;
  }

  header {
    display: none;
    margin: var(--gutter-l) 0;
    grid-template-columns: max-content 1fr;
    column-gap: 10mm;

    img {
      width: 30mm;
    }

    h1 {
      font-weight: 500;
    }
  }
}

.title-container {
  border-bottom: 2px solid var(--color-grey-70);
  .heading:first-of-type {
    margin-bottom: var(--gutter-s) !important;
  }
}

.body-container {
  padding-top: var(--gutter-m);
  display: grid;
  grid-auto-flow: row;
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  .page {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: white;
    border: none;
    padding: 15mm 20mm 35mm 20mm;

    header {
      display: grid;
    }

    &__used {
      .attribute label.label.horizontal {
        grid-template-columns: 1fr 1fr auto;
      }
    }
  }

  #app__terms,
  .print__hide {
    display: none;
  }
}
</style>
